import { Dayjs } from 'dayjs';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { FilterChip } from 'v2/components/SearchAndFilters/SearchAndFilters';
import { formatDate } from 'v2/utils/dateTime';
import { EventDeviceOption, EventVisitorOption, getEventAccessLabel, getEventDetailLabel } from './eventFilterOptions';
import { EventsFilterState } from './eventFilterTypes';

interface Params {
  translation: Translation;
  filters: EventsFilterState;
  visitorOptions: EventVisitorOption[];
  deviceOptions: EventDeviceOption[];
}

const getDateRangeLabel = (dateStart: Dayjs | null, dateEnd: Dayjs | null, translation: Translation): string => {
  const start = dateStart && formatDate(dateStart.toDate(), 'dateTimeShort');
  const end = dateEnd && formatDate(dateEnd.toDate(), 'dateTimeShort');
  if (start && end) return `${start} – ${end}`;
  if (start) return `${translation.from_date} ${start}`;
  return `${translation.until_date} ${end}`;
};

export const createEventFilterChips = ({
  translation,
  filters,
  visitorOptions,
  deviceOptions
}: Params): FilterChip[] => {
  const { access, detail, dateStart, dateEnd, user, device } = filters.filter;
  const { setFilterAccess, setFilterDetail, clearFilterDates, setFilterUser, setFilterDevice } = filters.setters;

  const chips: FilterChip[] = [];

  if (access !== null) {
    chips.push({
      label: getEventAccessLabel(access, translation),
      onDelete: () => setFilterAccess(null)
    });
  }

  if (user !== null) {
    chips.push({
      label: visitorOptions.find((option) => option.id === user)?.label || user,
      onDelete: () => setFilterUser(null)
    });
  }

  if (device !== null) {
    chips.push({
      label: deviceOptions.find((option) => option.id === device)?.label || device,
      onDelete: () => setFilterDevice(null)
    });
  }

  if (detail !== null) {
    chips.push({
      label: getEventDetailLabel(detail, translation),
      onDelete: () => setFilterDetail(null)
    });
  }

  if (dateStart !== null || dateEnd !== null) {
    chips.push({
      label: getDateRangeLabel(dateStart, dateEnd, translation),
      onDelete: clearFilterDates
    });
  }

  return chips;
};
